// Shared event-label helpers for the audit surface.
//
// Lifted out of _authenticated.audit.index.tsx + the order-detail
// AuditTrailPanel so the audit PDF document can reuse the same human
// labels — keeps the on-screen audit view and the PDF in sync without
// either side duplicating string maps.

// Entity-grouped event_type catalogue. Drawn from (a) the canonical
// list in migrations/audit_events.sql comment block and (b) the
// event_types actually emitted in the codebase. New event_types
// render fine without an entry here (AuditEventList falls back to
// the raw dotted name); adding them just makes them filterable
// from the audit browse UI.
export const EVENT_TYPE_GROUPS: ReadonlyArray<{
  label: string;
  values: readonly string[];
}> = [
  {
    label: 'Order',
    values: [
      'order.created',
      'order.assigned',
      'order.unassigned',
      'order.reassigned',
      'order.picked_up',
      'order.delivered',
      'order.attempted',
      'order.failed',
      'order.cancelled',
      'order.returned',
      'order.completed',
      'order.retrieval_initiated',
      'order.phi_accessed',
      'order.deconsolidated',
      'orders.bulk_uploaded',
    ],
  },
  {
    label: 'Route',
    values: [
      'route.created',
      'route.published',
      'route.unpublished',
      'route.edited',
      'route.completed',
      'route.driver_assigned',
      'route.driver_unassigned',
      'route.orders_added',
      'route.orders_removed',
      'route.stops_reordered',
    ],
  },
  {
    label: 'Stop',
    values: ['stop.reassigned', 'stop.completed', 'stop.skipped'],
  },
  {
    label: 'Delivery / POD',
    values: [
      'delivery.geofence_overridden',
      'delivery.barcode_skipped',
      'pod.captured',
      'pod.image_validation_failed',
    ],
  },
  {
    label: 'Driver',
    values: [
      'driver.created',
      'driver.updated',
      'driver.reactivated',
      'driver.on_leave',
      'driver.suspended',
      'driver.terminated',
      'driver.shift_started',
      'driver.shift_completed',
      'driver.reoptimized',
    ],
  },
  {
    label: 'Patient / PHI',
    values: [
      'patient.created',
      'patient.matched',
      'patient.phi_accessed',
      'user.sensitive_login',
    ],
  },
  {
    label: 'Access / Settings',
    values: [
      'pharmacy_access.granted',
      'pharmacy_access.revoked',
      'pharmacy.settings_changed',
      'tenant.settings_changed',
      'subscription.tier_changed',
    ],
  },
  {
    label: 'Retrieval',
    values: ['retrieval.initiated', 'retrieval.in_progress', 'retrieval.completed'],
  },
];

// Entity-type dropdown options, same source as audit browse.
export const ENTITY_TYPE_OPTIONS = [
  'order',
  'route',
  'stop',
  'driver',
  'delivery',
  'pod',
  'patient',
  'pharmacy',
  'pharmacy_access',
  'retrieval',
  'tenant',
  'user',
  'subscription',
] as const;

// "<entity> <verb>" from a dotted event_type, with underscores flattened.
// "pharmacy_access.granted" -> "Pharmacy access granted".
// AuditEventList falls back to the raw dotted name when no label
// callback is provided, so this is purely a readability layer.
export function humanizeEventType(t: string): string {
  const idx = t.indexOf('.');
  if (idx === -1) return t;
  const entity = t.slice(0, idx).replace(/_/g, ' ');
  const verb = t.slice(idx + 1).replace(/_/g, ' ');
  if (!entity || !verb) return t;
  return entity.charAt(0).toUpperCase() + entity.slice(1) + ' ' + verb;
}

// Per-entity narrow label maps. The order-detail timeline uses
// ORDER_EVENT_LABELS to render slightly friendlier headings than
// humanizeEventType produces ("Order delivered" vs "Order delivered"
// is the same; "Order assigned to route" vs "Order assigned" is the
// interesting case). The PDF picks up the same map.
export const ORDER_EVENT_LABELS: Record<string, string> = {
  'order.created':              'Order created',
  'order.assigned':             'Order assigned to route',
  'order.picked_up':            'Order picked up',
  'order.delivered':            'Order delivered',
  'order.attempted':            'Delivery attempted',
  'order.failed':               'Order delivery failed',
  'order.returned':             'Order returned',
  'order.cancelled':            'Order cancelled',
  'order.reassigned':           'Order reassigned to a different pharmacy',
  'order.retrieval_initiated':  'Marked for retrieval',
  // Routine-but-logged. Heading reads as a record-keeping note, not
  // an incident.
  'order.phi_accessed':         'Patient information accessed',
};

// Label function used by per-order timelines (order detail page + PDF).
// Falls back to humanizeEventType for any event_type not in the narrow map.
export function orderEventLabel(t: string): string {
  return ORDER_EVENT_LABELS[t] ?? humanizeEventType(t);
}
